import { useState, useEffect, useMemo, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Shield,
  DollarSign,
  MessageSquare,
  Zap,
  Save,
  Lock,
  AlertTriangle,
  FileDown,
} from 'lucide-react'
import { useAppDispatch } from '@/redux/hooks'
import { getRoomDetails, updateRoomDetails } from '@/redux/asyncThunks/room'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from '@/redux/store'
import { toast } from 'sonner'
import { GameRoomStatus, ResourceEnforcementMode } from '@/redux/types/room'
import RoomNotFound from '@/components/PageNotFound/RoomNotFound'
import {
  InfoTooltip,
  LimitInput,
} from '@/components/settings/ResourceLimits/LimitInput'
import { ResourceLimitsSkeleton } from '@/components/settings/ResourceLimits/ResourceLimitsSkeleton'
import { ViewPlayerLimitsCard } from '@/components/settings/ResourceLimits/ViewPlayerLimitsCard'
import { ResourceLimitsHeader } from '@/components/settings/ResourceLimits/ResourceLimitsHeader'
import { ImportModal } from '@/components/Room/ImportModal'

export default function ResourceLimitsSettings() {
  const dispatch = useAppDispatch()
  const params = useParams()
  const roomId = params.roomId as string

  const { currentRoom, isLoading, error } = useSelector(
    (state: RootState) => state.room
  )

  const isGameCompleted = useMemo(
    () => currentRoom?.status === GameRoomStatus.COMPLETED,
    [currentRoom?.status]
  )

  const [isUpdatingResourceLimits, setIsUpdatingResourceLimits] =
    useState(false)
  const [defaultLimits, setDefaultLimits] = useState<{
    currency_amount: number | null
    message_limit: number | null
    token_limit: number | null
  }>({
    currency_amount: null,
    message_limit: null,
    token_limit: null,
  })
  const [showDefaultLimitsForm, setShowDefaultLimitsForm] = useState(false)
  const [pendingEnable, setPendingEnable] = useState(false)
  const [importResourceLimitsOpen, setImportResourceLimitsOpen] =
    useState<boolean>(false)

  useEffect(() => {
    if (roomId) {
      dispatch(getRoomDetails(roomId))
    }
  }, [dispatch, roomId])

  useEffect(() => {
    if (currentRoom) {
      setDefaultLimits({
        currency_amount: currentRoom.default_currency_amount ?? null,
        message_limit: currentRoom.default_message_limit ?? null,
        token_limit: currentRoom.default_token_limit ?? null,
      })
      setShowDefaultLimitsForm(
        currentRoom.resource_limits_enabled || pendingEnable
      )
    }
  }, [currentRoom, pendingEnable])

  const handleResourceLimitsToggle = useCallback(
    async (checked: boolean) => {
      if (isGameCompleted) return

      if (checked && !currentRoom?.resource_limits_enabled) {
        setShowDefaultLimitsForm(true)
        setPendingEnable(true)
        toast.error(
          'Please save default limits first (even if set to unlimited) before enabling resource limits. Click "Save Default Limits" to proceed.'
        )
        return
      }

      setIsUpdatingResourceLimits(true)
      try {
        await dispatch(
          updateRoomDetails({
            roomId,
            resource_limits_enabled: checked,
          })
        ).unwrap()

        if (checked) {
          setPendingEnable(false)
        }

        toast.success(
          `Resource limits ${checked ? 'enabled' : 'disabled'} successfully!`
        )
      } catch (error) {
        toast.error('Failed to update resource limits setting')
      } finally {
        setIsUpdatingResourceLimits(false)
      }
    },
    [dispatch, isGameCompleted, roomId, defaultLimits, currentRoom]
  )

  const handleEnforcementModeChange = useCallback(
    async (mode: ResourceEnforcementMode) => {
      if (isGameCompleted) return

      setIsUpdatingResourceLimits(true)
      try {
        await dispatch(
          updateRoomDetails({
            roomId,
            resource_enforcement_mode: mode,
          })
        ).unwrap()

        toast.success(
          `Enforcement mode updated to ${mode === ResourceEnforcementMode.BLOCK ? 'Block' : 'Warn'}`
        )
      } catch (error) {
        toast.error('Failed to update enforcement mode')
      } finally {
        setIsUpdatingResourceLimits(false)
      }
    },
    [dispatch, isGameCompleted, roomId]
  )

  const handleSaveDefaultLimits = useCallback(async () => {
    if (isGameCompleted) return

    setIsUpdatingResourceLimits(true)
    try {
      const updatePayload: {
        default_currency_amount?: number | null
        default_message_limit?: number | null
        default_token_limit?: number | null
        resource_limits_enabled?: boolean
      } = {
        default_currency_amount: defaultLimits.currency_amount,
        default_message_limit: defaultLimits.message_limit,
        default_token_limit: defaultLimits.token_limit,
      }

      if (pendingEnable) {
        updatePayload.resource_limits_enabled = true
      }

      await dispatch(
        updateRoomDetails({
          roomId,
          ...updatePayload,
        })
      ).unwrap()

      if (pendingEnable) {
        setPendingEnable(false)
        toast.success(
          'Default limits saved and resource limits enabled successfully!'
        )
      } else {
        toast.success('Default limits updated successfully!')
      }
    } catch (error) {
      if (pendingEnable) {
        toast.error('Failed to save default limits and enable resource limits')
      } else {
        toast.error('Failed to update default limits')
      }
      setDefaultLimits({
        currency_amount: currentRoom?.default_currency_amount ?? null,
        message_limit: currentRoom?.default_message_limit ?? null,
        token_limit: currentRoom?.default_token_limit ?? null,
      })
    } finally {
      setIsUpdatingResourceLimits(false)
    }
  }, [
    dispatch,
    isGameCompleted,
    roomId,
    defaultLimits,
    currentRoom,
    pendingEnable,
  ])

  if (isLoading) {
    return <ResourceLimitsSkeleton />
  }
  if (error)
    return (
      <div className='flex items-center justify-center w-full h-full text-center'>
        <RoomNotFound message='Unable to fetch the details.' />
      </div>
    )
  if (!currentRoom) return <div>No room details found.</div>

  return (
    <div className='relative px-6 md:px-12 lg:px-24 py-6 space-y-6 max-w-6xl mx-auto'>
      <ResourceLimitsHeader
        roomId={roomId}
        title='Resource Limits Settings'
        isGameCompleted={isGameCompleted}
      />

      {!isGameCompleted && (
        <div className='flex justify-end'>
          <Button
            type='button'
            variant='outline'
            className='border-dark-blue text-dark-blue hover:bg-slate-50'
            onClick={() => setImportResourceLimitsOpen(true)}
          >
            <FileDown className='w-4 h-4 mr-2' />
            Import resource limits
          </Button>
        </div>
      )}

      <Card className='border-2 shadow-lg transition-all duration-300 hover:shadow-xl'>
        <CardHeader className='pb-4'>
          <div className='flex items-start justify-between gap-4'>
            <div className='flex-1 min-w-0'>
              <div className='flex items-center gap-2 mb-2'>
                <CardTitle className='text-xl font-semibold text-slate-800'>
                  Resource Limits
                </CardTitle>
                <InfoTooltip
                  content={
                    <p>
                      These limits will be used as default limits for the
                      players that are going to be invited. But later on these
                      limits can be changed in per player configuration and per
                      player config will have precedence over these.
                    </p>
                  }
                />
              </div>
              <CardDescription className='text-sm text-slate-600'>
                Enable resource limits (messages, tokens, or currency) for
                players. Configure default limits that apply to newly invited
                players.
              </CardDescription>
            </div>
            <div className='flex items-center gap-2 flex-shrink-0'>
              <Switch
                checked={currentRoom?.resource_limits_enabled ?? false}
                onCheckedChange={handleResourceLimitsToggle}
                disabled={isGameCompleted || isUpdatingResourceLimits}
                className='data-[state=checked]:bg-dark-blue'
              />
              <InfoTooltip
                content={
                  <p>
                    When enabled, players will have resource limits (messages,
                    tokens, or currency) that restrict their interactions with
                    advisors. Limits can be configured per player.
                  </p>
                }
              />
            </div>
          </div>
        </CardHeader>

        {(currentRoom?.resource_limits_enabled || showDefaultLimitsForm) && (
          <CardContent className='space-y-6 pt-0'>
            <div className='p-4 bg-slate-50/50 rounded-lg border border-slate-200'>
              <div className='flex flex-col sm:flex-row sm:items-center gap-3'>
                <div className='flex items-center gap-2 min-w-[160px]'>
                  <Shield className='h-4 w-4 text-slate-600' />
                  <Label className='text-sm font-semibold text-slate-700'>
                    Enforcement Mode:
                  </Label>
                </div>
                <div className='flex items-center gap-3 flex-1'>
                  <Select
                    value={
                      currentRoom?.resource_enforcement_mode ||
                      ResourceEnforcementMode.BLOCK
                    }
                    onValueChange={handleEnforcementModeChange}
                    disabled={isGameCompleted || isUpdatingResourceLimits}
                  >
                    <SelectTrigger className='w-full sm:w-[220px] bg-white border-slate-300 focus:border-dark-blue'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ResourceEnforcementMode.BLOCK}>
                        <div className='flex items-center gap-2'>
                          <Lock size={14} />
                          Block Action
                        </div>
                      </SelectItem>
                      <SelectItem value={ResourceEnforcementMode.WARN}>
                        <div className='flex items-center gap-2'>
                          <AlertTriangle size={14} />
                          Warn Only
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <InfoTooltip
                    content={
                      <div className='space-y-1'>
                        <p>
                          <strong>Block:</strong> Prevents actions when limits
                          are exceeded.
                        </p>
                        <p>
                          <strong>Warn:</strong> Allows actions but tracks
                          overage.
                        </p>
                      </div>
                    }
                  />
                </div>
              </div>
            </div>

            {pendingEnable && (
              <div className='p-4 bg-amber-50 border border-amber-200 rounded-lg'>
                <div className='flex items-start gap-2'>
                  <AlertTriangle className='h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0' />
                  <div className='flex-1'>
                    <p className='text-sm font-semibold text-amber-800 mb-1'>
                      Default Limits Required
                    </p>
                    <p className='text-sm text-amber-700'>
                      Please set and save default limits (even if set to
                      unlimited) before enabling resource limits. Click "Save
                      Default Limits" to proceed. This ensures all players
                      receive proper default limits when resource limits are
                      enabled.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className='space-y-4'>
              <div className='flex items-center gap-2 pb-2 border-b border-slate-200'>
                <h3 className='text-sm font-semibold text-slate-700'>
                  Default Limits
                </h3>
                <span className='text-xs text-slate-500'>
                  (Applied to newly invited players)
                </span>
                {pendingEnable && (
                  <span className='text-xs text-amber-600 font-medium'>
                    * Required before enabling
                  </span>
                )}
              </div>

              <div className='grid gap-4 md:grid-cols-1'>
                <div className='p-4 bg-gradient-to-r from-sky-50/50 to-indigo-50/50 rounded-lg border border-sky-100 hover:border-sky-200 transition-colors'>
                  <LimitInput
                    label='Default Currency:'
                    value={defaultLimits.currency_amount}
                    onChange={(value) =>
                      setDefaultLimits((prev) => ({
                        ...prev,
                        currency_amount: value,
                      }))
                    }
                    placeholder='Unlimited'
                    disabled={isGameCompleted}
                    tooltipContent={
                      <p>
                        Sets the default currency balance for newly invited
                        players. You can override this later with individual
                        player limits. Leave empty to allow unlimited currency,
                        or set to 0 to not allow any currency.
                      </p>
                    }
                    message='Starting balance for new players'
                    icon={DollarSign}
                  />
                </div>

                <div className='p-4 bg-gradient-to-r from-green-50/50 to-emerald-50/50 rounded-lg border border-green-100 hover:border-green-200 transition-colors'>
                  <LimitInput
                    label='Default Messages:'
                    value={defaultLimits.message_limit}
                    onChange={(value) =>
                      setDefaultLimits((prev) => ({
                        ...prev,
                        message_limit: value,
                      }))
                    }
                    placeholder='Unlimited'
                    disabled={isGameCompleted}
                    tooltipContent={
                      <p>
                        Sets the default number of messages for newly invited
                        players. Individual player limits can be customized
                        separately. Leave empty for unlimited messages, or set
                        to 0 to not allow any messages.
                      </p>
                    }
                    message='Message allowance per player'
                    icon={MessageSquare}
                  />
                </div>

                <div className='p-4 bg-gradient-to-r from-purple-50/50 to-pink-50/50 rounded-lg border border-purple-100 hover:border-purple-200 transition-colors'>
                  <LimitInput
                    label='Default Tokens:'
                    value={defaultLimits.token_limit}
                    onChange={(value) =>
                      setDefaultLimits((prev) => ({
                        ...prev,
                        token_limit: value,
                      }))
                    }
                    placeholder='Unlimited'
                    disabled={isGameCompleted}
                    tooltipContent={
                      <div className='space-y-2'>
                        <p>
                          Sets the default token allowance for newly invited
                          players. You can adjust limits per player if needed.
                          Leave empty to allow unlimited tokens, or set to 0 to
                          not allow any tokens.
                        </p>
                        <div className='pt-2 border-t border-slate-600'>
                          <p className='font-semibold mb-1'>
                            Recommended ranges:
                          </p>
                          <p>
                            Minimum: 500–800 tokens Balanced: 1,000–1,500 tokens
                          </p>
                        </div>
                      </div>
                    }
                    message='Token budget for AI interactions'
                    icon={Zap}
                    additionalContent={
                      <>
                        <span className='font-medium'>Recommended:</span>{' '}
                        Minimum 500–800 tokens, Balanced 1,000–1,500 tokens
                      </>
                    }
                  />
                </div>
              </div>
            </div>

            <div className='flex justify-end'>
              <Button
                type='button'
                onClick={handleSaveDefaultLimits}
                disabled={isGameCompleted || isUpdatingResourceLimits}
                className='bg-dark-blue hover:bg-dark-blue-hovered shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                {isUpdatingResourceLimits ? (
                  <>
                    <div className='h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2' />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className='h-4 w-4 mr-2' />
                    Save Default Limits
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        )}
      </Card>

      {currentRoom?.resource_limits_enabled && (
        <ViewPlayerLimitsCard roomId={roomId} />
      )}

      {importResourceLimitsOpen && roomId && (
        <ImportModal
          key={`${roomId}-resource-limits-import`}
          isOpen={importResourceLimitsOpen}
          onClose={() => setImportResourceLimitsOpen(false)}
          scope='resource_limits'
          roomId={roomId}
          roomName={currentRoom.roomname?.toString()}
          onImportComplete={() => {
            setImportResourceLimitsOpen(false)
            void dispatch(getRoomDetails(roomId))
          }}
        />
      )}
    </div>
  )
}
